import {
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';
import {
    AiAssistApiError,
    AiAssistService,
    AI_ASSIST_ICON,
} from '@/app/services/ai-assist.service';

const DEFAULT_INSTRUCTION =
    'Improve clarity and fix grammar. Keep the original tone, meaning and language.';

type Mode = 'suggest' | 'refine';

/**
 * Single "AI" affordance for the SEO description, merging suggest + rewrite:
 * one button decides by whether the field already has text.
 * - empty  → `suggest` mode: drafts fresh options from the brand identity
 *   (`POST /text/suggest-branding`).
 * - filled → `refine` mode: rewrites the existing text on an instruction
 *   (`POST /text/refine`), same as the generic refine affordance.
 *
 * Either way the operator picks one option, emitted via `(applied)`.
 */
@Component({
    selector: 'app-ai-seo-assist',
    standalone: true,
    imports: [FormsModule, Button, Dialog, InputText, Message, ProgressSpinner, Tooltip],
    templateUrl: './ai-seo-assist.html',
})
export class AiSeoAssist {
    private readonly aiAssist = inject(AiAssistService);

    /** Current SEO description — decides suggest vs refine, and is the refine input. */
    readonly text = input<string>('');
    readonly maxLength = input<number | null>(160);
    // Suggest-mode seed context:
    readonly brandName = input<string>('');
    readonly domain = input<string>('');
    readonly vertical = input<string | null>(null);
    readonly style = input<Record<string, unknown> | null>(null);
    readonly contextDisplayName = input<string | null>(null);
    readonly contextName = input<string | null>(null);

    /** Disable the trigger (service down) + the why. */
    readonly disabled = input<boolean>(false);
    readonly disabledReason = input<string | null>(null);

    readonly applied = output<string>();

    protected readonly aiIcon = AI_ASSIST_ICON;

    protected readonly visible = signal(false);
    protected readonly mode = signal<Mode>('suggest');
    protected readonly loading = signal(false);
    protected readonly error = signal<string | null>(null);
    protected readonly instruction = signal('');
    protected readonly candidates = signal<
        { text: string; primary: boolean }[]
    >([]);

    /** Trigger is enabled if we can either refine existing text or seed a suggest. */
    protected readonly hasInput = computed(
        () =>
            this.text().trim().length > 0 || this.brandName().trim().length > 0,
    );

    protected open(): void {
        if (this.disabled() || !this.hasInput()) return;
        this.mode.set(this.text().trim() ? 'refine' : 'suggest');
        this.error.set(null);
        this.candidates.set([]);
        this.instruction.set(DEFAULT_INSTRUCTION);
        this.visible.set(true);
        if (this.mode() === 'suggest') void this.run();
    }

    protected onVisibleChange(value: boolean): void {
        this.visible.set(value);
    }

    protected async run(): Promise<void> {
        if (this.loading()) return;
        if (this.mode() === 'refine' && !this.instruction().trim()) return;
        this.loading.set(true);
        this.error.set(null);
        try {
            if (this.mode() === 'refine') {
                const res = await this.aiAssist.refine({
                    text: this.text(),
                    instruction: this.instruction().trim(),
                    maxLength: this.maxLength(),
                });
                this.candidates.set(
                    this.dedupe([
                        { text: res.refined, primary: true },
                        ...res.alternatives.map((a) => ({
                            text: a,
                            primary: false,
                        })),
                    ]),
                );
            } else {
                const res = await this.aiAssist.suggestBranding({
                    brandName: this.brandName(),
                    domain: this.domain(),
                    vertical: this.vertical(),
                    style: this.style(),
                    context: {
                        displayName: this.contextDisplayName(),
                        name: this.contextName(),
                    },
                });
                this.candidates.set(
                    this.dedupe(
                        (res.drafts.seo_description ?? []).map((t) => ({
                            text: t,
                            primary: false,
                        })),
                    ),
                );
            }
        } catch (err) {
            this.error.set(this.friendlyError(err));
        } finally {
            this.loading.set(false);
        }
    }

    protected use(value: string): void {
        this.applied.emit(value);
        this.visible.set(false);
    }

    private dedupe(
        items: { text: string; primary: boolean }[],
    ): { text: string; primary: boolean }[] {
        const seen = new Set<string>();
        const out: { text: string; primary: boolean }[] = [];
        for (const item of items) {
            const trimmed = item.text.trim();
            if (!trimmed || seen.has(trimmed)) continue;
            seen.add(trimmed);
            out.push({ text: trimmed, primary: item.primary });
        }
        return out;
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service. Please try again.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'The AI model took too long to respond. Please try again.';
            case 'vendor_unavailable':
                return 'The AI model is temporarily unavailable. Please try again shortly.';
            case 'rate_limited':
                return 'Too many requests right now. Please wait a moment and retry.';
            case 'content_moderated':
                return 'The request was blocked by the content filter. Try rephrasing.';
            case 'unauthorized':
                return 'Your session is not authorized. Please sign in again.';
            case 'invalid_request':
                return err.message ?? 'The request was rejected as invalid.';
            default:
                return err.message ?? 'Something went wrong. Please try again.';
        }
    }
}
